const ConsoleContent = () => {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-semibold text-blue-800 mb-6">
        Use the Console to run queries
      </h1>
      <img src="" className="size-2/3" />

      <b> Custom Query Search</b>
      <p>
        The custom query search functionality works in the provided image and
        how it can be used effectively for log monitoring and security event
        detection without relying on Wazuh.
      </p>

      <b> How It Works</b>
      <p>
        The query search feature enables users to filter and retrieve specific
        security events from a dataset. Users can input search conditions, such
        as rule levels, agent names, or event descriptions, to refine the
        results. The system processes the query and displays the matching events
        in a tabular format.
      </p>

      <b>Usage of “: (Colon)” in Query Language :</b>
      <img src="" className="size-2/3" />

      <b>Operators in Query Language :</b>
      <img src="" className="size-2/3" />

      <b>Features</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li className="font-bold">Search Bar</li>
        <li className=" ml-6">
          Users can enter search queries using logical operators (e.g., AND,
          OR).
        </li>
        <li className=" ml-6">
          {" "}
          Example: rule.level &gt; 10 AND agent.name: (agent1 OR agent2)
        </li>
        <li className="font-bold">Download as Excel</li>
        <li className="ml-6">
          {" "}
          Allows exporting the displayed results into an Excel file for further
          analysis.
        </li>
        <li className="font-bold">Results Table</li>
        <li className="ml-6">
          {" "}
          Displays event logs based on the query conditions.
        </li>
        <li className="ml-6"> Columns include:</li>
        <li className="ml-12">
          <b>Time:</b> Timestamp of the event occurrence.
        </li>
        <li className="ml-12">
          <b>Agent:</b> Unique agent identifier.
        </li>
        <li className="ml-12">
          <b>Agent Name: </b> Name of the agent generating the event.
        </li>
        <li className="ml-12">
          <b>Technique(s)</b> Attack techniques, if applicable.
        </li>
        <li className="ml-12">
          <b>Tactic(s)</b> Attack tactics associated with the event.
        </li>
        <li className="ml-12">
          <b>Description:</b> A brief summary of the event.
        </li>
        <li className="ml-12">
          <b>Level:</b> Severity level of the event.
        </li>
        <li className="ml-12">
          <b>Rule ID:</b> The corresponding rule ID for the detected event.
        </li>
        <li className="font-bold">Pagination</li>
        <li className="ml-6">
          Allows users to navigate through multiple pages of results.
        </li>
        <li className="ml-6">Provides options to jump to a specific page.</li>
      </ul>

      <b>Use Cases</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li className="font-bold">Threat Detection</li>
        <li className=" ml-6">
          Security teams can filter logs to identify critical alerts, such as
          anomaly detection events or unauthorized access attempts.
        </li>

        <li className="font-bold">Incident Investigation</li>
        <li className="ml-6">
          Analysts can retrieve historical logs related to a specific agent or
          event type to perform forensic analysis.
        </li>
        <li className="font-bold">Compliance Auditing</li>
        <li className="ml-6">
          Organizations can use queries to extract logs that align with
          compliance requirements and generate reports
        </li>

        <li className="font-bold">Performance Monitoring</li>
        <li className="ml-6">
          IT administrators can track agent activities and system events to
          ensure smooth operation and security compliance.
        </li>
      </ul>

      <b>Example Queries</b>
      <ul className="flex flex-col gap-4 list-decimal ml-6">
        <li>
          <b> Find all critical alerts:</b> rule.level &gt;= 7
        </li>
        <li>
          <b>Retrieve events for a specific agent:</b> agent.name: agent1
        </li>
        <li>
          <b>Find events containing a specific keyword in the description: </b>
          description: "Wazuh agent started"
        </li>
        <li>
          <b>Filter events by rule ID:</b> rule.id: 503
        </li>
      </ul>

      <img src="" className="size-2/3" />
      <p>
        The custom query search functionality provides an efficient way to
        analyze security event logs without relying on Wazuh. It enhances
        security monitoring, incident response, and compliance tracking through
        its powerful filtering capabilities. By utilizing logical query
        structures, users can gain deeper insights into their security landscape
        and take necessary actions accordingly.
      </p>

      <p>
        Security alerts categorized by their Alert ID, Description, Level, and
        Count. This type of data is critical for security monitoring, as it
        helps in identifying potential threats and system issues as shown in
        below image
      </p>
      <img src="" className="size-2/3" />

      <b>Authentication Failures:</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li>
          Several alerts are related to failed authentication attempts (
          <span className="text-green-600">
            syslog: User authentication failure, sshd: authentication failed
          </span>
          ).
        </li>
        <li>
          Some of these failures have high occurrence counts (eg.,
          <span className="text-green-600 ml-1">
            sshd: authentication failed
          </span>{" "}
          - 2516 times ).
        </li>
      </ul>

      <b>Brute Force Attempts:</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li>
          Alerts such as
          <span className="text-green-600 mx-1">
            sshd: brute force trying to get access to the system
          </span>
          indicate repeated login failures, likely from an automated attack.
        </li>
        <li>
          High severity levels (
          <span className="text-green-600 mx-1">Level 10</span>) suggest these
          are critical security concerns.
        </li>
      </ul>

      <b> System Changes and Package Installations:</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li>
          Alerts related to package installations (
          <span className="text-green-600 mx-1">
            New dpkg (Debian Package) installed, Dpkg removed
          </span>
          ) provide visibility into system modifications.
        </li>
        <li>
          These changes should be monitored to prevent unauthorized software
          installations.
        </li>
      </ul>

      <b>Multiple Logon Failures:</b>
      <ul className="list-disc ml-6">
        <li>
          Repeated failed logins, both in <b>Linux (PAM, SSHD)</b> and &nbsp;
          <b>Windows environments</b>, indicate potential brute-force attacks or
          misconfigured authentication policies.
        </li>
      </ul>

      <b>Recommendations for Security Teams</b>
      <ul className="flex flex-col gap-4 list-disc ml-6">
        <li className="font-bold">Monitor High-Level Alerts (Level 7-10):</li>
        <li className="ml-6">
          Prioritize investigating and mitigating brute-force attempts and
          authentication failures.
        </li>
        <li className="font-bold">Correlate Events:</li>
        <li className="ml-6">
          Look for patterns where failed logins are followed by successful
          unauthorized access.
        </li>
        <li className="font-bold">Enhance Logging & Alerting:</li>
        <li className="ml-6">
          Ensure logs capture sufficient details to trace the source of attacks.
        </li>
        <li className="ml-6">
          Set up real-time alerting mechanisms to notify administrators of
          unusual activity.
        </li>
        <li className="font-bold">
          Implement Stronger Authentication Methods:
        </li>
        <li className="ml-6">
          Enforce multi-factor authentication (MFA) to reduce the risk of
          compromised credentials.
        </li>
      </ul>

      <p>
        The security alert log analysis highlights the importance of continuous
        monitoring and query-based event filtering. By leveraging the query
        search capabilities, security teams can swiftly detect, investigate, and
        mitigate security incidents, ensuring a robust security posture for the
        system.
      </p>
    </div>
  );
};

export default ConsoleContent;
